import React, { useCallback, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, ExternalLink, Grid, FileText, ToggleLeft, ToggleRight, ZoomIn, ZoomOut, Minimize2, Maximize2 } from 'lucide-react';
import * as XLSX from 'xlsx';
import ExcelViewer from './ExcelViewer';
import ExcelViewerFormatted from './ExcelViewerFormatted';
import { parseJsonViewerContent } from '../utils/jsonViewer';
import { usePdfSearch, PdfSearchControl, PdfPageHighlights } from './pdfSearch';
import * as pdfjsLib from 'pdfjs-dist';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const FileViewerDocument = ({
  url: propUrl,
  fileType: propFileType,
  fileName,
  initialPage,
  portalKey,
  isOpen,
  isActive,
  onMinimize,
  onClose,
  onStatusChange,
}) => {
  // For DEMO org, the fetch functions (fetchPdfFromBlob, fetchTextFromBlob) already
  // fetch the demo PDF and return a blob URL. We only need to override the fileType
  // to ensure it's rendered as a PDF.
  const isDemoOrg = localStorage.getItem('currentOrgCode') === 'DEMO';

  const url = propUrl;
  const fileType = isDemoOrg && (!propFileType || propFileType === 'pdf') ? 'pdf' : propFileType;

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [textContent, setTextContent] = useState('');
  const [mobileDataUrl, setMobileDataUrl] = useState(null);
  const [pdfLoadedSuccessfully, setPdfLoadedSuccessfully] = useState(false);
  const [excelData, setExcelData] = useState(null);
  const [selectedSheet, setSelectedSheet] = useState(0);
  const [excelViewMode, setExcelViewMode] = useState('formatted'); // 'formatted' or 'table' - default to formatted view
  const [jsonTables, setJsonTables] = useState([]);
  const [selectedJsonTable, setSelectedJsonTable] = useState(0);
  const [jsonViewMode, setJsonViewMode] = useState('json');
  const [htmlContent, setHtmlContent] = useState('');
  const iframeRef = useRef(null);

  // PDF.js state
  const [pdfDocument, setPdfDocument] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [pdfScale, setPdfScale] = useState(1.5);
  const [visiblePage, setVisiblePage] = useState(1);
  const [pageSizes, setPageSizes] = useState([]);
  const canvasRefsMap = useRef({});
  const scrollContainerRef = useRef(null);
  const renderedPagesRef = useRef(new Set());
  const observerRef = useRef(null);
  const renderTasksRef = useRef({});
  const initialScrollDoneRef = useRef(false);

  // Set CSV files to default to table view
  useEffect(() => {
    if (fileType === 'csv') {
      setExcelViewMode('table');
    }
  }, [fileType]);
  const loadingTimeoutRef = useRef(null);
  const fallbackTimeoutRef = useRef(null);

  // In-viewer PDF text search (shared with FileViewerPanel) — canvas pages have
  // no text layer, so browser Ctrl+F can't see PDF content. Only bind while
  // this viewer is the open, active document.
  const pdfSearch = usePdfSearch({
    pdfDocument, totalPages, pdfScale, scrollContainerRef,
    enabled: fileType === 'pdf' && !!isOpen && !!isActive,
  });

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    setTextContent('');
    setMobileDataUrl(null);
    setPdfLoadedSuccessfully(false);
    setExcelData(null);
    setSelectedSheet(0);
    setJsonTables([]);
    setSelectedJsonTable(0);
    setJsonViewMode('json');
    setHtmlContent('');
    setPdfDocument(null);
    setTotalPages(0);
    setVisiblePage(1);
    setPageSizes([]);
    canvasRefsMap.current = {};
    renderedPagesRef.current.clear();
    initialScrollDoneRef.current = false;
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    Object.values(renderTasksRef.current).forEach(task => {
      try { task.cancel(); } catch (e) {}
    });
    renderTasksRef.current = {};
  }, [url, fileType]);

  // Detect mobile devices
  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           window.innerWidth <= 768;
  };

  // Convert blob URL to data URL for better mobile compatibility
  const convertBlobToDataUrl = async (blobUrl) => {
    try {
      const response = await fetch(blobUrl);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error converting blob to data URL:', error);
      return null;
    }
  };

  useEffect(() => {
    let cancelled = false;

    if (fileType === 'json') {
      setIsLoading(true);
      fetch(url)
        .then(res => {
          if (!res.ok) {
            throw new Error(`Failed to fetch JSON content: ${res.statusText}`);
          }
          return res.text();
        })
        .then(text => {
          if (cancelled) return;
          try {
            const parsed = parseJsonViewerContent(text);
            setTextContent(parsed.formatted);
            setJsonTables(parsed.tables);
            setSelectedJsonTable(0);
            setJsonViewMode(parsed.tables.length ? 'table' : 'json');
          } catch (_) {
            setTextContent(text);
            setJsonTables([]);
            setSelectedJsonTable(0);
            setJsonViewMode('json');
          }
          setIsLoading(false);
        })
        .catch(err => {
          if (cancelled) return;
          console.error('Error fetching JSON content:', err);
          setError(err.message);
          setIsLoading(false);
        });
    } else if (fileType === 'text') {
      setIsLoading(true);
      fetch(url)
        .then(res => {
          if (!res.ok) {
            throw new Error(`Failed to fetch text content: ${res.statusText}`);
          }
          return res.text();
        })
        .then(text => {
          if (cancelled) return;
          setTextContent(text);
          setIsLoading(false);
        })
        .catch(err => {
          if (cancelled) return;
          console.error('Error fetching text content:', err);
          setError(err.message);
          setIsLoading(false);
        });
    } else if (fileType === 'html') {
      setIsLoading(true);
      fetch(url)
        .then(res => {
          if (!res.ok) {
            throw new Error(`Failed to fetch HTML content: ${res.statusText}`);
          }
          return res.text();
        })
        .then(html => {
          if (cancelled) return;
          setHtmlContent(html);
          setIsLoading(false);
        })
        .catch(err => {
          if (cancelled) return;
          console.error('Error fetching HTML content:', err);
          setError(err.message);
          setIsLoading(false);
        });
    } else if (fileType === 'csv') {
      setIsLoading(true);
      fetch(url)
        .then(res => {
          if (!res.ok) {
            throw new Error(`Failed to fetch CSV content: ${res.statusText}`);
          }
          return res.arrayBuffer();
        })
        .then(buffer => {
          if (cancelled) return;
          // Parse CSV using xlsx library (it supports CSV). raw: cells stay the
          // file's own text — typed parsing turned "2026-09-17" into a UTC date
          // that rendered a day early (9/16/26) in US timezones.
          const workbook = XLSX.read(buffer, {
            type: 'array',
            raw: true
          });
          const sheets = workbook.SheetNames.map(name => ({
            name,
            data: XLSX.utils.sheet_to_json(workbook.Sheets[name], {
              header: 1,
              raw: false,
              dateNF: 'mm/dd/yyyy',
              defval: '' // Use empty string for empty cells
            })
          }));
          setExcelData(sheets);
          setIsLoading(false);
        })
        .catch(err => {
          if (cancelled) return;
          console.error('Error fetching CSV content:', err);
          setError(err.message);
          setIsLoading(false);
        });
    } else if (fileType === 'excel') {
      setIsLoading(true);
      fetch(url)
        .then(res => {
          if (!res.ok) {
            throw new Error(`Failed to fetch Excel content: ${res.statusText}`);
          }
          return res.arrayBuffer();
        })
        .then(buffer => {
          if (cancelled) return;
          const workbook = XLSX.read(buffer, { 
            type: 'array',
            cellStyles: true,
            cellDates: true,
            cellNF: true,
            sheetStubs: true
          });
          const sheets = workbook.SheetNames.map(name => ({
            name,
            data: XLSX.utils.sheet_to_json(workbook.Sheets[name], { 
              header: 1,
              raw: false,
              dateNF: 'mm/dd/yyyy'
            })
          }));
          setExcelData(sheets);
          setIsLoading(false);
        })
        .catch(err => {
          if (cancelled) return;
          console.error('Error fetching Excel content:', err);
          setError(err.message);
          setIsLoading(false);
        });
    } else if (fileType === 'pdf') {
      // Use PDF.js to render PDFs directly to canvas
      setIsLoading(true);
      console.log('📄 PDF.js: Loading PDF from URL', url?.substring(0, 50) + '...');

      const loadPdf = async () => {
        try {
          const loadingTask = pdfjsLib.getDocument(url);
          const pdf = await loadingTask.promise;
          if (cancelled) {
            pdf.destroy();
            return;
          }
          console.log('📄 PDF.js: Loaded PDF with', pdf.numPages, 'pages');
          setPdfDocument(pdf);
          setTotalPages(pdf.numPages);
          setVisiblePage(1);
          setIsLoading(false);
        } catch (err) {
          if (cancelled) return;
          console.error('❌ PDF.js: Error loading PDF:', err);
          setError(`Failed to load PDF: ${err.message}`);
          setIsLoading(false);
        }
      };

      loadPdf();
    }

    return () => {
      cancelled = true;
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current);
      }
    };
  }, [url, fileType]);

  // Compute page sizes when document loads or scale changes
  useEffect(() => {
    if (!pdfDocument || totalPages === 0) return;

    const loadSizes = async () => {
      const sizes = [];
      for (let i = 1; i <= totalPages; i++) {
        const page = await pdfDocument.getPage(i);
        const viewport = page.getViewport({ scale: pdfScale });
        sizes.push({ width: Math.floor(viewport.width), height: Math.floor(viewport.height) });
      }
      renderedPagesRef.current.clear();
      setPageSizes(sizes);
    };

    loadSizes();
  }, [pdfDocument, pdfScale, totalPages]);

  // Render a single PDF page to its canvas
  const renderPdfPage = async (pageNum) => {
    const canvas = canvasRefsMap.current[pageNum];
    if (!canvas || !pdfDocument) return;
    if (renderedPagesRef.current.has(pageNum)) return;

    renderedPagesRef.current.add(pageNum);

    try {
      const page = await pdfDocument.getPage(pageNum);
      const viewport = page.getViewport({ scale: pdfScale });
      const dpr = window.devicePixelRatio || 1;
      const context = canvas.getContext('2d');

      canvas.width = Math.floor(viewport.width * dpr);
      canvas.height = Math.floor(viewport.height * dpr);
      canvas.style.width = Math.floor(viewport.width) + 'px';
      canvas.style.height = Math.floor(viewport.height) + 'px';
      context.setTransform(dpr, 0, 0, dpr, 0, 0);

      const renderTask = page.render({ canvasContext: context, viewport });
      renderTasksRef.current[pageNum] = renderTask;
      await renderTask.promise;
      delete renderTasksRef.current[pageNum];
    } catch (err) {
      if (err.name !== 'RenderingCancelledException') {
        console.error('Error rendering page', pageNum, err);
      }
      renderedPagesRef.current.delete(pageNum);
      delete renderTasksRef.current[pageNum];
    }
  };

  // Set up IntersectionObserver for lazy page rendering
  useEffect(() => {
    if (pageSizes.length === 0 || !scrollContainerRef.current || !pdfDocument) return;

    if (observerRef.current) observerRef.current.disconnect();

    // Cancel any in-flight render tasks
    Object.values(renderTasksRef.current).forEach(task => {
      try { task.cancel(); } catch (e) {}
    });
    renderTasksRef.current = {};

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const pageNum = parseInt(entry.target.dataset.page);
            if (!isNaN(pageNum)) renderPdfPage(pageNum);
          }
        });
      },
      {
        root: scrollContainerRef.current,
        rootMargin: '500px 0px',
        threshold: 0
      }
    );

    const wrappers = scrollContainerRef.current.querySelectorAll('[data-page]');
    wrappers.forEach(el => observer.observe(el));
    observerRef.current = observer;

    return () => {
      observer.disconnect();
      Object.values(renderTasksRef.current).forEach(task => {
        try { task.cancel(); } catch (e) {}
      });
    };
  }, [pageSizes, pdfDocument, pdfScale]);

  // Track visible page on scroll for page indicator
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || pageSizes.length === 0) return;

    const handleScroll = () => {
      const wrappers = container.querySelectorAll('[data-page]');
      const containerTop = container.scrollTop;
      const containerCenter = containerTop + container.clientHeight / 2;

      let closestPage = 1;
      let closestDistance = Infinity;

      wrappers.forEach(wrapper => {
        const wrapperCenter = wrapper.offsetTop + wrapper.offsetHeight / 2;
        const distance = Math.abs(containerCenter - wrapperCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestPage = parseInt(wrapper.dataset.page);
        }
      });

      setVisiblePage(closestPage);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [pageSizes]);

  // Scroll to initialPage once page layout is ready (only on first render)
  useEffect(() => {
    if (!initialPage || pageSizes.length === 0 || !scrollContainerRef.current) return;
    if (initialScrollDoneRef.current) return;
    initialScrollDoneRef.current = true;
    const timer = setTimeout(() => {
      const target = scrollContainerRef.current?.querySelector(`[data-page="${initialPage}"]`);
      if (target) {
        target.scrollIntoView({ block: 'start' });
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [pageSizes, initialPage]);

  // Cleanup PDF document, observer, and render tasks on unmount
  useEffect(() => {
    return () => {
      if (pdfDocument) {
        pdfDocument.destroy();
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      Object.values(renderTasksRef.current).forEach(task => {
        try { task.cancel(); } catch (e) {}
      });
    };
  }, [pdfDocument]);

  const handleIframeLoad = () => {
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
    }
    setIsLoading(false);
    setError(null);
  };

  const handleIframeError = () => {
    console.error('❌ PDF iframe failed to load:', url);
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
    }
    setIsLoading(false);
    setError('PDF viewer blocked by browser. Click "Open in New Tab" to view the PDF.');
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = url;
    let extension = 'pdf';
    if (fileType === 'text') extension = 'txt';
    else if (fileType === 'json') extension = 'json';
    else if (fileType === 'html') extension = 'html';
    else if (fileType === 'excel') extension = 'xlsx';
    else if (fileType === 'csv') extension = 'csv';
    link.download = `document.${extension}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenInNewTab = () => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    onStatusChange?.({
      fileName: fileName || 'File Viewer',
      fileType,
      visiblePage,
      totalPages,
    });
  }, [fileName, fileType, visiblePage, totalPages, onStatusChange]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
            <p className="text-gray-600">Loading File...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <div className="text-center max-w-md mx-auto p-6">
            <div className="text-red-600 mb-4">
              <svg className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to load file</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <div className="flex gap-2 justify-center">
              <button
                onClick={handleDownload}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <Download className="h-4 w-4 mr-2" />
                Download
              </button>
              <button
                onClick={handleOpenInNewTab}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Open in New Tab
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (fileType === 'json') {
      return (
        <div className="w-full h-full flex flex-col bg-slate-950 overflow-hidden">
          <div className="flex-shrink-0 border-b border-slate-800 bg-slate-900 px-4 py-2">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm text-slate-200">
                <FileText className="h-4 w-4" />
                <span>JSON Preview</span>
              </div>
              <div className="flex items-center gap-2">
                {jsonTables.length > 0 && (
                  <select
                    value={selectedJsonTable}
                    onChange={(event) => setSelectedJsonTable(Number(event.target.value))}
                    className="h-8 max-w-[320px] rounded-md border border-slate-700 bg-slate-950 px-2 text-xs text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    title="JSON table"
                  >
                    {jsonTables.map((table, index) => (
                      <option key={`${table.name}-${index}`} value={index}>
                        {table.name} ({table.sourceRowCount}{table.truncated ? '+' : ''})
                      </option>
                    ))}
                  </select>
                )}
                <div className="inline-flex rounded-md border border-slate-700 overflow-hidden">
                  <button
                    onClick={() => setJsonViewMode('json')}
                    className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium ${
                      jsonViewMode === 'json' ? 'bg-blue-600 text-white' : 'bg-slate-950 text-slate-200 hover:bg-slate-800'
                    }`}
                  >
                    <FileText className="h-3.5 w-3.5" />
                    JSON
                  </button>
                  <button
                    onClick={() => setJsonViewMode('table')}
                    disabled={jsonTables.length === 0}
                    className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed ${
                      jsonViewMode === 'table' ? 'bg-blue-600 text-white' : 'bg-slate-950 text-slate-200 hover:bg-slate-800'
                    }`}
                  >
                    <Grid className="h-3.5 w-3.5" />
                    Tables
                  </button>
                </div>
              </div>
            </div>
          </div>
          {jsonViewMode === 'table' && jsonTables.length > 0 ? (
            <div className="flex-1 min-h-0 bg-white">
              <ExcelViewer
                data={jsonTables}
                selectedSheet={selectedJsonTable}
                onSheetChange={setSelectedJsonTable}
              />
            </div>
          ) : (
            <pre className="flex-1 min-h-0 p-5 overflow-auto text-sm leading-6 text-emerald-100 font-mono whitespace-pre-wrap break-words">
              {textContent}
            </pre>
          )}
        </div>
      );
    }

    if (fileType === 'text') {
      return (
        <pre className="w-full h-full p-4 overflow-auto bg-gray-50 text-sm text-gray-800 whitespace-pre-wrap break-words">
          {textContent}
        </pre>
      );
    }

    if (fileType === 'html') {
      return (
        <div className="w-full h-full overflow-hidden bg-white">
          <iframe
            srcDoc={htmlContent}
            className="w-full h-full border-none"
            title="HTML Viewer"
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
            style={{ background: 'white' }}
          />
        </div>
      );
    }

    if (fileType === 'excel' || fileType === 'csv') {
      return (
        <div className="w-full h-full flex flex-col bg-white overflow-hidden">
          {/* Excel/CSV toolbar with view mode toggle */}
          <div className="flex-shrink-0 border-b border-gray-200 bg-gray-50 px-4 py-2">
            <div className="flex items-center justify-between">
              {/* Sheet tabs */}
              {excelData && excelData.length > 1 && (
                <div className="flex gap-2 overflow-x-auto">
                  {excelData.map((sheet, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedSheet(index)}
                      className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                        selectedSheet === index
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                      }`}
                    >
                      {sheet.name}
                    </button>
                  ))}
                </div>
              )}

              {/* View mode toggle - only show formatted view for Excel files */}
              <div className="flex items-center space-x-3">
                <span className="text-sm text-gray-700">View:</span>
                <div className="flex items-center space-x-2">
                  {fileType === 'excel' && (
                    <button
                      onClick={() => setExcelViewMode('formatted')}
                      className={`inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                        excelViewMode === 'formatted'
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                      }`}
                    >
                      <FileText className="h-3 w-3 mr-1" />
                      Formatted
                    </button>
                  )}
                  <button
                    onClick={() => setExcelViewMode('table')}
                    className={`inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      excelViewMode === 'table'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                    }`}
                  >
                    <Grid className="h-3 w-3 mr-1" />
                    Table
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Excel/CSV viewer content */}
          <div className="flex-grow relative">
            {excelViewMode === 'formatted' && fileType === 'excel' ? (
              <ExcelViewerFormatted
                url={url}
                fileName="Excel File"
              />
            ) : (
              excelData ? (
                <ExcelViewer
                  data={excelData}
                  selectedSheet={selectedSheet}
                  onSheetChange={setSelectedSheet}
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading {fileType === 'csv' ? 'CSV' : 'Excel'} data for table view...</p>
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      );
    }

    // Mobile-specific PDF handling - use native browser PDF viewer
    if (isMobile() && fileType === 'pdf') {
      const pdfUrl = mobileDataUrl || url;
      
      return (
        <div className="w-full h-full flex flex-col bg-white">
          {/* Mobile PDF notice */}
          <div className="flex-shrink-0 bg-blue-50 border-b border-blue-200 p-4">
            <div className="flex items-center">
              <div className="text-blue-600 mr-3">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-blue-800 text-sm">
                Tap "Open PDF" for the best mobile viewing experience
              </p>
            </div>
          </div>
          
          {/* PDF viewer area */}
          <div className="flex-grow relative bg-gray-100 flex items-center justify-center">
            {/* Attempt iframe first */}
            <iframe
              ref={iframeRef}
              src={pdfUrl}
              className="absolute inset-0 w-full h-full border-none"
              title="PDF Viewer"
              onLoad={() => {

                // Mark PDF as loaded successfully
                setPdfLoadedSuccessfully(true);
                setIsLoading(false);
                
                // Clear the fallback timeout since PDF loaded
                if (fallbackTimeoutRef.current) {
                  clearTimeout(fallbackTimeoutRef.current);

                }
                
                setTimeout(() => {

                }, 500);
              }}
              onError={() => {

                setError('mobile_fallback');
              }}
              style={{ backgroundColor: 'white' }}
            />
            
            {/* Mobile fallback UI */}
            {error === 'mobile_fallback' && (
              <div className="absolute inset-0 flex items-center justify-center p-6">
                <div className="text-center max-w-sm">
                  <div className="text-gray-400 mb-4">
                    <svg className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <p className="text-gray-600 mb-4 text-sm">
                    This PDF can't be displayed in the mobile browser viewer.
                  </p>
                  <button
                    onClick={() => {
                      // Open PDF in new window which should trigger native mobile PDF viewer
                      window.open(pdfUrl, '_blank', 'noopener,noreferrer');
                    }}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open PDF
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    // PDF rendering using PDF.js with continuous scroll
    return (
      <div className="w-full h-full flex flex-col bg-gray-100">
        {/* PDF continuous scroll container */}
        <div
          ref={scrollContainerRef}
          className="flex-grow overflow-auto"
        >
          {pdfDocument && pageSizes.length > 0 ? (
            <div className="flex flex-col items-center py-4 gap-4">
              {pageSizes.map((size, index) => (
                <div
                  key={index + 1}
                  data-page={index + 1}
                  style={{ width: size.width, height: size.height }}
                  className="flex-shrink-0 shadow-lg bg-white relative"
                >
                  <canvas
                    ref={el => { if (el) canvasRefsMap.current[index + 1] = el; }}
                    style={{ display: 'block' }}
                  />
                  <PdfPageHighlights search={pdfSearch} page={index + 1} scale={pdfScale} />
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
                <p className="text-gray-600">Loading PDF...</p>
              </div>
            </div>
          )}
        </div>
        {/* Floating page indicator */}
        {totalPages > 0 && pageSizes.length > 0 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white text-sm px-3 py-1.5 rounded-full pointer-events-none z-10">
            Page {visiblePage} of {totalPages}
          </div>
        )}
      </div>
    );
  };

  const modalContent = (
      <div
        className={`fixed inset-0 bg-black/75 justify-center items-center ${isOpen && isActive ? 'flex' : 'hidden'}`}
        style={{
          zIndex: 999999
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
      <div className={`relative bg-white shadow-xl flex flex-col ${
        isMobile() 
          ? 'w-full h-full rounded-none' // Full screen on mobile
          : 'w-[95%] h-[95%] max-w-7xl rounded-lg' // Windowed on desktop
      }`} style={{ overflow: 'hidden' }}>
        <div className="absolute top-0 right-0 flex items-center gap-1 p-2 bg-white/80 backdrop-blur rounded-bl-lg z-10">
           {/* PDF zoom controls - only show for PDF files */}
           {fileType === 'pdf' && pdfDocument && (
             <>
               <PdfSearchControl search={pdfSearch} />
               <div className="w-px h-6 bg-gray-300 mx-1" />
               <button
                 onClick={() => setPdfScale(s => Math.max(0.5, s - 0.25))}
                 disabled={pdfScale <= 0.5}
                 className="p-2 text-gray-600 hover:text-gray-900 focus:outline-none rounded-full hover:bg-gray-100 disabled:opacity-50"
                 title="Zoom out"
               >
                 <ZoomOut size={20} />
               </button>
               <span className="text-sm text-gray-600 min-w-[50px] text-center">
                 {Math.round(pdfScale * 100)}%
               </span>
               <button
                 onClick={() => setPdfScale(s => Math.min(3, s + 0.25))}
                 disabled={pdfScale >= 3}
                 className="p-2 text-gray-600 hover:text-gray-900 focus:outline-none rounded-full hover:bg-gray-100 disabled:opacity-50"
                 title="Zoom in"
               >
                 <ZoomIn size={20} />
               </button>
               <div className="w-px h-6 bg-gray-300 mx-1" />
             </>
           )}
           <button
             onClick={handleDownload}
             className="p-2 text-gray-600 hover:text-gray-900 focus:outline-none rounded-full hover:bg-gray-100"
             title="Download File"
             aria-label="Download File"
           >
             <Download size={20} />
           </button>
           <button
             onClick={handleOpenInNewTab}
             className="p-2 text-gray-600 hover:text-gray-900 focus:outline-none rounded-full hover:bg-gray-100"
             title="Open in new tab"
             aria-label="Open in new tab"
           >
             <ExternalLink size={20} />
           </button>
           <button
             onClick={onMinimize}
             className="p-2 text-gray-600 hover:text-gray-900 focus:outline-none rounded-full hover:bg-gray-100"
             title="Minimize File Viewer"
             aria-label="Minimize File Viewer"
           >
             <Minimize2 size={20} />
           </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-600 hover:text-gray-900 focus:outline-none rounded-full hover:bg-gray-100"
              aria-label="Close"
            >
              <X size={20} />
            </button>
        </div>
        <div className="flex-grow relative overflow-hidden min-h-0 h-full">
         {renderContent()}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body, portalKey);
};

const createFileViewerTabId = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const getFileViewerTabKey = ({ url, fileType, fileName, initialPage }) => (
  `${url || ''}|${fileType || ''}|${fileName || ''}|${initialPage || ''}`
);

const getFileViewerTabIdentity = ({ url, fileType, fileName }) => {
  const stableName = String(fileName || '').trim().toLowerCase();
  if (stableName) {
    return `${fileType || ''}|${stableName}`;
  }
  return `${fileType || ''}|${url || ''}`;
};

const FileViewerTabDocument = ({ tab, isOpen, isActive, onMinimize, onCloseTab, updateStatus }) => {
  const handleClose = useCallback(() => {
    onCloseTab(tab.id);
  }, [onCloseTab, tab.id]);

  const handleStatusChange = useCallback((status) => {
    updateStatus(tab.id, status);
  }, [tab.id, updateStatus]);

  return (
    <FileViewerDocument
      url={tab.url}
      fileType={tab.fileType}
      fileName={tab.fileName}
      initialPage={tab.initialPage}
      portalKey={tab.id}
      isOpen={isOpen}
      isActive={isActive}
      onMinimize={onMinimize}
      onClose={handleClose}
      onStatusChange={handleStatusChange}
    />
  );
};

const FileViewerModal = ({ url, fileType, fileName, onClose, initialPage }) => {
  const [tabs, setTabs] = useState([]);
  const [activeTabId, setActiveTabId] = useState(null);
  const [isOpen, setIsOpen] = useState(true);
  const [statuses, setStatuses] = useState({});
  const lastOpenedKeyRef = useRef(null);

  useEffect(() => {
    if (!url) return;

    const tabKey = getFileViewerTabKey({ url, fileType, fileName, initialPage });
    const tabIdentity = getFileViewerTabIdentity({ url, fileType, fileName });
    if (lastOpenedKeyRef.current === tabIdentity) {
      setTabs(previousTabs => previousTabs.map(tab => (
        tab.identity === tabIdentity && tab.url !== url
          ? { ...tab, key: tabKey, url, fileType, fileName, initialPage }
          : tab
      )));
      setIsOpen(true);
      return;
    }
    lastOpenedKeyRef.current = tabIdentity;

    setTabs(previousTabs => {
      const existingTab = previousTabs.find(tab => tab.identity === tabIdentity || tab.key === tabKey);
      if (existingTab) {
        setActiveTabId(existingTab.id);
        setIsOpen(true);
        return previousTabs.map(tab => (
          tab.id === existingTab.id && tab.url !== url
            ? { ...tab, key: tabKey, identity: tabIdentity, url, fileType, fileName, initialPage }
            : tab
        ));
      }

      const nextTab = {
        id: createFileViewerTabId(),
        key: tabKey,
        identity: tabIdentity,
        url,
        fileType,
        fileName,
        initialPage,
      };
      setActiveTabId(nextTab.id);
      setIsOpen(true);
      return [...previousTabs, nextTab];
    });
  }, [url, fileType, fileName, initialPage]);

  const updateStatus = useCallback((tabId, status) => {
    setStatuses(previousStatuses => ({
      ...previousStatuses,
      [tabId]: {
        ...previousStatuses[tabId],
        ...status,
      },
    }));
  }, []);

  const revokeTabUrl = useCallback((tab) => {
    if (!tab?.url?.startsWith?.('blob:')) return;
    try {
      URL.revokeObjectURL(tab.url);
    } catch (_) {
      // Blob URLs can already be revoked by parent cleanup.
    }
  }, []);

  const closeTab = useCallback((tabId) => {
    setTabs(previousTabs => {
      const closedTab = previousTabs.find(tab => tab.id === tabId);
      const nextTabs = previousTabs.filter(tab => tab.id !== tabId);

      revokeTabUrl(closedTab);
      setStatuses(previousStatuses => {
        const nextStatuses = { ...previousStatuses };
        delete nextStatuses[tabId];
        return nextStatuses;
      });

      if (nextTabs.length === 0) {
        setActiveTabId(null);
        setIsOpen(false);
        setTimeout(() => onClose?.(), 0);
      } else if (activeTabId === tabId) {
        setActiveTabId(nextTabs[nextTabs.length - 1].id);
      }

      return nextTabs;
    });
  }, [activeTabId, onClose, revokeTabUrl]);

  const closeAllTabs = useCallback(() => {
    tabs.forEach(revokeTabUrl);
    setTabs([]);
    setStatuses({});
    setActiveTabId(null);
    setIsOpen(false);
    onClose?.();
  }, [onClose, revokeTabUrl, tabs]);

  const minimizeViewer = useCallback(() => {
    setIsOpen(false);
  }, []);

  if (tabs.length === 0) {
    return null;
  }

  const dockContent = createPortal(
    <div className="fixed inset-x-0 bottom-0 z-[1000000] pointer-events-none">
      <div className="mx-auto flex w-full max-w-7xl items-end gap-1 overflow-x-auto px-4 pb-0 sm:px-6">
        {tabs.map(tab => {
          const status = statuses[tab.id] || {};
          const label = status.fileName || tab.fileName || 'File Viewer';
          const statusFileType = status.fileType || tab.fileType;
          const isActive = tab.id === activeTabId;
          const isActiveOpen = isActive && isOpen;

          return (
            <div
              key={tab.id}
              className={`pointer-events-auto inline-flex min-w-0 max-w-[360px] items-stretch rounded-t-lg border border-b-0 shadow-2xl ${
                isActiveOpen
                  ? 'border-blue-500 bg-blue-600 text-white'
                  : 'border-slate-700 bg-slate-950 text-white'
              }`}
            >
              <button
                onClick={() => {
                  setActiveTabId(tab.id);
                  setIsOpen(true);
                }}
                className={`inline-flex min-w-0 items-center gap-2 px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  isActiveOpen ? 'hover:bg-blue-500' : 'hover:bg-slate-900'
                }`}
                title={`Open ${label}`}
                aria-label={`Open ${label}`}
              >
                <Maximize2 size={15} className="flex-shrink-0" />
                <span className="truncate">{label}</span>
                {statusFileType === 'pdf' && status.totalPages > 0 && (
                  <span className="flex-shrink-0 rounded-full bg-white/10 px-2 py-0.5 text-xs text-slate-200">
                    Page {status.visiblePage || 1}
                  </span>
                )}
              </button>
              <button
                onClick={() => closeTab(tab.id)}
                className={`flex-shrink-0 border-l px-2.5 text-white/80 hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  isActiveOpen ? 'border-blue-400' : 'border-slate-700'
                }`}
                title={`Close ${label}`}
                aria-label={`Close ${label}`}
              >
                <X size={16} />
              </button>
            </div>
          );
        })}
        {tabs.length > 1 && (
          <button
            onClick={closeAllTabs}
            className="pointer-events-auto inline-flex items-center gap-2 rounded-t-lg border border-b-0 border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-xl hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            title="Close all file tabs"
            aria-label="Close all file tabs"
          >
            <X size={14} />
            Close All
          </button>
        )}
      </div>
    </div>,
    document.body
  );

  return (
    <>
      {tabs.map(tab => (
        <FileViewerTabDocument
          key={tab.id}
          tab={tab}
          isOpen={isOpen}
          isActive={tab.id === activeTabId}
          onMinimize={minimizeViewer}
          onCloseTab={closeTab}
          updateStatus={updateStatus}
        />
      ))}
      {dockContent}
    </>
  );
};

export default FileViewerModal;
